'use memo'

import { useContext, useEffect, useState } from 'react'
import { MenuTitles } from '../../constants/menu'
import { ContentLayout, PageLayout } from 'layout'
import { useParams } from 'react-router-dom'
import { isEqualWith } from 'lodash-es'
import {
  AgxButton,
  AgxDatePicker,
  AgxDropdown,
  AgxSpacer,
  AgxTextInput,
  AgxTextArea,
  AgxTypography,
  useToast,
} from '@urbanx/agx.ui-components2'

import { FormPrompt } from 'components/FormPrompt'
import { isDirtyCompare } from 'utils/compares'
import './agencyDetails.scss'
import { AuthContext } from '@urbanx/agx.auth.client'
import {
  AgencyDetailsModel,
  AgencyDetailsModelAccountType,
  Agreements,
  LicenceDetailsState,
  useGetAgencyDetails,
  useInvalidateUseGetAgencyDetails,
  useInvalidateUseGetAllAgencies,
  useUpdateAgency,
} from '@urbanx/agx.agencies.client'
import { useSelectedAgency } from 'hooks'
import { agencyStateOptions } from './agencyStateOptions'
import { allStateOptions } from './allStateOptions'
import { agencyAccountTypeOptions } from './agencyAccountTypeOptions'
import { getAgreementOptions } from './getAgreementOptions'
import {
  createCompanyLicence,
  getLicenceStateOptions,
  getNextAvailableState,
  sanitizeAgencyDetails,
  stripCompanyLicenceUuid,
  withCompanyLicenceUuids,
} from './helpers'
import { CompanyLicenseDetails } from './types'

const AgencyDetailsPage = () => {
  const { userAccount } = useContext(AuthContext)
  const [isFormDirty, setIsFormDirty] = useState(false)
  const [formLoaded, setFormLoaded] = useState(false)
  const { agencyId } = useParams()
  const [companyLicences, setCompanyLicences] = useState<
    CompanyLicenseDetails[]
  >([])
  const { agency: selectedAgency } = useSelectedAgency()

  const invalidateGetAllAgencies = useInvalidateUseGetAllAgencies()
  const invalidateGetAgencyDetails = useInvalidateUseGetAgencyDetails()

  const [agencyInformation, setAgencyInformation] =
    useState<AgencyDetailsModel>({})

  const { data: agencyDetails, isPending: agencyDetailsPending } =
    useGetAgencyDetails(agencyId!, !!agencyId)

  const { pushToast } = useToast()

  const { updateAgency } = useUpdateAgency({
    onSuccess: async () => {
      setIsFormDirty(false)
      await invalidateGetAllAgencies()
      await invalidateGetAgencyDetails(agencyId!)

      pushToast({
        type: 'success',
        message: `${MenuTitles.AGENCY_DETAILS} updated successfully`,
      })
    },
    onError: () => {
      pushToast({
        type: 'error',
        message: `Error updating ${MenuTitles.AGENCY_DETAILS}`,
      })
    },
  })

  const addLicence = () => {
    const nextState = getNextAvailableState(companyLicences)
    const newLicence = createCompanyLicence(nextState)

    setCompanyLicences([...companyLicences, newLicence])
  }

  const removeLicence = (uuid: string) => {
    const updatedLicences = companyLicences.filter((l) => l.uuid !== uuid)
    setCompanyLicences(updatedLicences)
  }

  const updateCompanyLicences = (licence: CompanyLicenseDetails) => {
    if (companyLicences) {
      const updatedLicences = [
        ...companyLicences.map((l) => (l.uuid === licence.uuid ? licence : l)),
      ]
      setCompanyLicences(updatedLicences)

      setAgencyInformation((previousState) => ({
        ...previousState,
        companyDetails: {
          ...previousState.companyDetails,
          licences: updatedLicences.map(stripCompanyLicenceUuid),
        },
      }))
    }
  }

  useEffect(() => {
    if (!agencyDetailsPending && agencyDetails) {
      setAgencyInformation(agencyDetails)
      if (agencyDetails.companyDetails?.licences) {
        setCompanyLicences(
          withCompanyLicenceUuids(agencyDetails.companyDetails.licences)
        )
      }

      setIsFormDirty(false)

      if (agencyDetails.id != null) setFormLoaded(true)
    }
  }, [agencyDetails, agencyDetailsPending])

  useEffect(() => {
    const isEqual = (a: unknown, b: unknown) =>
      isEqualWith(a, b, isDirtyCompare)

    const dirty = !isEqual(agencyDetails, agencyInformation)
    setIsFormDirty(dirty)
  }, [agencyInformation, agencyDetails])

  const addressStateDefaultValue =
    agencyInformation?.address?.state ?? allStateOptions[0]?.value ?? ''

  if (agencyInformation == null) return null

  const {
    tradingName,
    accountType,
    companyDetails: { licenseeName, abn, acn } = {},
    listingsEmail,
    listingsPhone,
    address: { streetAddress, suburb, postalCode } = {},
    instructionsConditions,
  } = agencyInformation

  const saveChanges = () => {
    updateAgency(sanitizeAgencyDetails(agencyInformation, companyLicences))
  }

  if (agencyDetails === undefined || agencyDetailsPending || !formLoaded)
    return null

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={selectedAgency?.name || ''}
      currentPageTitle={MenuTitles.AGENCY_DETAILS}
    >
      <ContentLayout
        hasSecondaryMenu={true}
        secondaryActiveMenu={MenuTitles.AGENCY_DETAILS}
      >
        <FormPrompt hasUnsavedChanges={isFormDirty} />
        <AgxSpacer direction="vertical" gap="spacing-5xl">
          <AgxSpacer
            direction="horizontal"
            contentAlignment="center"
            contentJustification="space-between"
            className="borderBottomContainer"
          >
            <AgxTypography id="agency-details-title" type="title" size="large">
              {MenuTitles.AGENCY_DETAILS}
            </AgxTypography>
            <AgxButton
              id="agency-save-changes"
              text="Save Changes"
              size="medium"
              variant="primary"
              onClick={saveChanges}
            />
          </AgxSpacer>

          <AgxSpacer
            direction="vertical"
            gap="spacing-5xl"
            className="container50Percent"
          >
            <AgxSpacer className="borderBottomContainer">
              <AgxTypography
                id="agency-details-company-details"
                type="label"
                size="large"
              >
                Company details
              </AgxTypography>
            </AgxSpacer>
            <AgxSpacer>
              <AgxTextInput
                id="tradingName"
                label="Trading Name"
                type="text"
                stretch
                hideOptionalText
                defaultValue={tradingName || ''}
                onChange={(value) =>
                  setAgencyInformation((previousState) => ({
                    ...previousState,
                    tradingName: value,
                  }))
                }
              />
            </AgxSpacer>
            <AgxSpacer direction="horizontal" gap="spacing-xl" fill>
              <AgxTextInput
                id="abn"
                label="ABN"
                type="abn"
                stretch
                hideOptionalText
                defaultValue={abn || ''}
                onChange={(value) =>
                  setAgencyInformation((previousState) => ({
                    ...previousState,
                    companyDetails: {
                      ...previousState.companyDetails,
                      abn: value,
                    },
                  }))
                }
              />

              <AgxTextInput
                id="acn"
                label="ACN"
                type="acn"
                stretch
                hideOptionalText
                defaultValue={acn || ''}
                onChange={(value) =>
                  setAgencyInformation((previousState) => ({
                    ...previousState,
                    companyDetails: {
                      ...previousState.companyDetails,
                      acn: value,
                    },
                  }))
                }
              />
            </AgxSpacer>
            <AgxSpacer>
              <AgxDropdown
                id="accountType"
                label="Account Type"
                stretch
                options={agencyAccountTypeOptions}
                hideOptionalText
                selected={accountType}
                onChange={(value) =>
                  setAgencyInformation((previousState) => ({
                    ...previousState,
                    accountType: value as AgencyDetailsModelAccountType,
                  }))
                }
              />
            </AgxSpacer>
            <AgxSpacer>
              <AgxTextInput
                id="licenseeName"
                label="Licensee name"
                type="text"
                stretch
                hideOptionalText
                defaultValue={licenseeName || ''}
                onChange={(value) =>
                  setAgencyInformation((previousState) => ({
                    ...previousState,
                    companyDetails: {
                      ...previousState.companyDetails,
                      licenseeName: value,
                    },
                  }))
                }
              />
            </AgxSpacer>
            {companyLicences?.map((licence, index) => {
              const agreementOptions = getAgreementOptions(`${licence.state}`)

              return (
                <AgxSpacer
                  key={licence.uuid}
                  direction="vertical"
                  gap="spacing-xl"
                >
                  <AgxSpacer
                    direction="horizontal"
                    contentJustification="space-between"
                    className="borderBottomContainer"
                  >
                    <AgxTypography
                      id={`agency-licence-heading-${licence.uuid}`}
                      type="label"
                      size="medium"
                    >
                      {`Licence ${index + 1}`}
                    </AgxTypography>
                    {companyLicences.length > 1 && (
                      <AgxButton
                        id={`remove-licence-${licence.uuid}`}
                        text={'Remove'}
                        size="small"
                        variant="hollow"
                        onClick={() => removeLicence(licence.uuid ?? '')}
                      />
                    )}
                  </AgxSpacer>
                  <AgxSpacer direction="horizontal" gap="spacing-xl" fill>
                    <AgxTextInput
                      id={`licenceNumber-${licence.uuid}`}
                      label="Licence Number"
                      type="text"
                      stretch
                      hideOptionalText
                      defaultValue={licence.licenceNumber || ''}
                      onChange={(value) =>
                        updateCompanyLicences({
                          licenceNumber: value,
                          expiryDate: licence.expiryDate,
                          state: licence.state,
                          agreements: licence.agreements,
                          uuid: licence.uuid,
                        })
                      }
                    />

                    <AgxDatePicker
                      id={`dtLicenseeExpiryDate-${licence.uuid}`}
                      label="Expiry Date"
                      noHeader={false}
                      stretch
                      optionalText=""
                      variant="compact"
                      value={licence.expiryDate ?? null}
                      onChange={(value) => {
                        updateCompanyLicences({
                          licenceNumber: licence.licenceNumber,
                          expiryDate: value ?? undefined,
                          state: licence.state,
                          agreements: licence.agreements,
                          uuid: licence.uuid,
                        })
                      }}
                    />
                  </AgxSpacer>
                  <AgxSpacer
                    direction="horizontal"
                    gap="spacing-xl"
                    fill
                    className="checkboxContainer"
                  >
                    <AgxDropdown
                      id={`companyState-${licence.uuid}`}
                      label="State"
                      options={getLicenceStateOptions(
                        licence,
                        companyLicences,
                        agencyStateOptions
                      )}
                      selected={
                        licence.state?.toString() ??
                        LicenceDetailsState.ACT.toString()
                      }
                      hideOptionalText
                      onChange={(value) => {
                        updateCompanyLicences({
                          licenceNumber: licence.licenceNumber,
                          expiryDate: licence.expiryDate,
                          state: value as unknown as LicenceDetailsState,
                          agreements: licence.agreements,
                          uuid: licence.uuid,
                        })
                      }}
                    />
                    <AgxDropdown
                      id={`agreements-${licence.uuid}`}
                      label="Agreements"
                      type="multi"
                      options={agreementOptions}
                      selected={
                        licence.agreements?.map((value) => value.toString()) ??
                        []
                      }
                      hideOptionalText
                      onChange={(value) => {
                        updateCompanyLicences({
                          licenceNumber: licence.licenceNumber,
                          expiryDate: licence.expiryDate,
                          state: licence.state,
                          agreements: value.map(
                            (agreement) => agreement as Agreements
                          ),
                          uuid: licence.uuid,
                        })
                      }}
                    />
                  </AgxSpacer>
                </AgxSpacer>
              )
            })}
            <AgxSpacer>
              <AgxButton
                id="add-licence"
                text={'Add Licence'}
                variant="primary"
                size="medium"
                disabled={companyLicences.length === 8}
                onClick={() => addLicence()}
              />
            </AgxSpacer>
          </AgxSpacer>
          <AgxSpacer
            direction="vertical"
            gap="spacing-5xl"
            className="container50Percent"
          >
            <AgxSpacer className="borderBottomContainer">
              <AgxTypography
                id="agency-details-contact-details"
                type="label"
                size="large"
              >
                Contact details
              </AgxTypography>
            </AgxSpacer>
            <AgxSpacer>
              <AgxTextInput
                id="listingsEmail"
                label="Listings Email"
                type="email"
                stretch
                hideOptionalText
                defaultValue={listingsEmail || ''}
                onChange={(value) =>
                  setAgencyInformation((previousState) => ({
                    ...previousState,
                    listingsEmail: value,
                  }))
                }
              />
            </AgxSpacer>
            <AgxSpacer>
              <AgxTextInput
                id="phoneNumber"
                label="Phone Number"
                type="phone"
                stretch
                hideOptionalText
                defaultValue={listingsPhone || ''}
                onChange={(value) =>
                  setAgencyInformation((previousState) => ({
                    ...previousState,
                    listingsPhone: value,
                  }))
                }
              />
            </AgxSpacer>
          </AgxSpacer>
          <AgxSpacer
            direction="vertical"
            gap="spacing-5xl"
            className="container50Percent"
          >
            <AgxSpacer className="borderBottomContainer">
              <AgxTypography
                id="agency-details-address"
                type="label"
                size="large"
              >
                Address
              </AgxTypography>
            </AgxSpacer>
            <AgxSpacer>
              <AgxTextInput
                id="streetAddress"
                label="Street Address"
                type="text"
                stretch
                hideOptionalText
                defaultValue={streetAddress || ''}
                onChange={(value) =>
                  setAgencyInformation((previousState) => ({
                    ...previousState,
                    address: {
                      ...previousState.address,
                      streetAddress: value,
                    },
                  }))
                }
              />
            </AgxSpacer>
            <AgxSpacer direction="horizontal" gap="spacing-xl" fill>
              <AgxTextInput
                id="suburb"
                label="Suburb"
                type="text"
                stretch
                hideOptionalText
                defaultValue={suburb || ''}
                onChange={(value) =>
                  setAgencyInformation((previousState) => ({
                    ...previousState,
                    address: {
                      ...previousState.address,
                      suburb: value,
                    },
                  }))
                }
              />

              <AgxDropdown
                id="state"
                label="State"
                options={allStateOptions}
                selected={addressStateDefaultValue}
                hideOptionalText
                onChange={(value) => {
                  setAgencyInformation((previousState) => ({
                    ...previousState,
                    address: {
                      ...previousState.address,
                      state: value,
                    },
                  }))
                }}
              />

              <AgxTextInput
                id="postcode"
                label="Postcode"
                type="text"
                stretch
                hideOptionalText
                defaultValue={postalCode || ''}
                onChange={(value) =>
                  setAgencyInformation((previousState) => ({
                    ...previousState,
                    address: {
                      ...previousState.address,
                      postalCode: value,
                    },
                  }))
                }
              />
            </AgxSpacer>
          </AgxSpacer>

          <AgxSpacer
            direction="vertical"
            gap="spacing-5xl"
            className="container50Percent"
          >
            <AgxSpacer className="borderBottomContainer">
              <AgxTypography
                id="agency-details-extras"
                type="label"
                size="large"
              >
                Extras
              </AgxTypography>
            </AgxSpacer>
            <AgxTextArea
              id="instructionsConditions"
              label="Reassignment Clause"
              defaultValue={instructionsConditions ?? ''}
              onInputValueChange={({ value }: { value: string }) =>
                setAgencyInformation((previousState) => ({
                  ...previousState,
                  instructionsConditions: value,
                }))
              }
              rows={6}
              maxLength={1100}
              hideOptionalText
              stretch
            />
          </AgxSpacer>
        </AgxSpacer>
      </ContentLayout>
    </PageLayout>
  )
}

export default AgencyDetailsPage
